import { Injectable, NgZone } from '@angular/core';
import * as amplitude from '@amplitude/analytics-browser';
import { WindowService } from '../window/window.service';
import { User } from 'spotlight-ui/app/models/user/user.model';
import { environment } from 'spotlight-ui/environments/environment';
import { VersionService } from '@spotlight/shared/version';
import { LocationScenarioService } from '../location-scenario';
import { stripLocationPrefix } from '../../shared/navbar/location-utils';
import { createLocationPrefixEnrichmentPlugin } from './strip-location-from-analytics';
import { createUiVersionEnrichmentPlugin } from '@spotlight/shared/analytics';

@Injectable()
export class AnalyticsService {

  constructor(public ws: WindowService, private readonly versionService: VersionService, private readonly ngZone: NgZone,
              private readonly locationScenario: LocationScenarioService) {
    this.ngZone.runOutsideAngular(() => {
      if (environment.amplitudeApiKey) {
        const version = this.versionService.getVersion();
        console.log('[AnalyticsService] Initializing Amplitude with appVersion:', version || '<empty>');
        // Added before init so events queued before init resolves are tagged too.
        amplitude.add(createUiVersionEnrichmentPlugin());
        const amplitudeInit = amplitude.init(environment.amplitudeApiKey, {
          appVersion: version,
          autocapture: {
            attribution: true,
            pageViews: true,
            sessions: true,
            formInteractions: true,
            fileDownloads: true,
            pageUrlEnrichment: true,
            elementInteractions: true,
            frustrationInteractions: {
              rageClicks: true,
              deadClicks: true,
              errorClicks: true,
              thrashedCursor: true,
            },
            networkTracking: true,
            webVitals: false,
            performanceTracking: false,
          },
        });
        // Queueing this plugin before init installs it first; Amplitude then adds pageUrlEnrichment
        // and overwrites page URL fields from location. Add after init so strip runs last.
        void amplitudeInit.promise.then(() => {
          amplitude.add(createLocationPrefixEnrichmentPlugin());
        });
      }
      this.registerReloadTracking();
    });
  }

  public sendEvent(title: string, payload: Record<string, any> = {}): void {
    // Simulated scenarios must not pollute product metrics.
    if (this.locationScenario.isSimulating) {
      return;
    }
    amplitude.track(title, payload);
  }

  private registerReloadTracking(): void {
    const window = this.ws.getWindow();
    const trackAppReload = () => {
      const navEntry = window.performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
      if (navEntry?.type !== 'reload') {
        return;
      }

      const strippedPath = stripLocationPrefix(window.location.pathname);
      this.sendEvent('App Reloaded', {
        page_url: `${window.location.origin}${strippedPath}${window.location.search}${window.location.hash}`,
      });
    };

    if (window.document.readyState === 'complete') {
      trackAppReload();
    } else {
      window.addEventListener('load', trackAppReload);
    }
  }

  public logPageView(screenName: string, modal: any): void {
    // Strip query params and hash fragments so page views don't exceed Amplitude's
    // unique event name limits when IDs are present in the URL
    let screen = screenName.split('?')[0];
    screen = screen.split('#')[0];
    screen = stripLocationPrefix(screen);
    this.sendEvent('Page Viewed', { screen, modal });
  }

  public setLocationAttributes(location: any): void {
    this.setUserAttribute('Restaurant Name', location.locationName);
    this.setUserAttribute('Restaurant ID', location.locationNumber);
  }

  public setUserAttribute(attributeName: string, value: any): void {
    const identifyEvent = new amplitude.Identify();
    identifyEvent.set(attributeName, value);
    amplitude.identify(identifyEvent);
  }

  public setUserIdentity(userName: string): void {
    amplitude.setUserId(userName);
  }

  public setup(user: User): void {
    this.setUserIdentity(user.userName);
  }
}
